import { Autocomplete, TextField, Typography, Box } from '@mui/material';
import { styled, lighten } from '@mui/system';
import React, { useCallback, useMemo } from 'react';
import { useStoreConfig } from '../contexts/StoreConfigContext';

// Styled components defined outside to prevent re-creation on each render
const GroupHeader = styled('div')(({ theme }) => ({
    position: 'sticky',
    top: '-8px',
    padding: '4px 10px',
    color: theme.palette.primary.main,
    backgroundColor: lighten(theme.palette.primary.light, 0.85),
}));

const GroupItems = styled('ul')({
    padding: 0,
});

interface BasicAutocompleteProps {
    name?: string;
    options: string[];
    value: string | string[];
    onChange: (newValue: string | string[]) => void;
    label: string;
    size?: 'small' | 'medium';
    fullWidth?: boolean;
    freeSolo?: boolean;
    disabled?: boolean;
    multiple?: boolean;
    required?: boolean;
}

export const BasicAutocomplete: React.FC<BasicAutocompleteProps> = ({
    options,
    value,
    onChange,
    label,
    size = 'medium',
    fullWidth = false,
    freeSolo = false,
    disabled = false,
    multiple = false,
    required = false,
}) => {
    const { config } = useStoreConfig();

    const groupedOptions = useMemo(() => options.map((option) => ({
        firstLetter: option.charAt(0).toUpperCase(),
        option,
    })).sort((a, b) => a.firstLetter.localeCompare(b.firstLetter)), [options]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && freeSolo) {
            const currentInputValue = (event.target as HTMLInputElement).value.trim();

            if (currentInputValue) {
                const capitalizedValue = currentInputValue.charAt(0).toUpperCase() + currentInputValue.slice(1);

                if (multiple) {
                    const newValues = Array.isArray(value) ? [...value, capitalizedValue] : [capitalizedValue];
                    onChange(newValues);
                    (event.target as HTMLInputElement).value = '';
                } else {
                    onChange(capitalizedValue);
                }
                event.preventDefault();
            }
        }
    }, [freeSolo, multiple, value, onChange]);

    const handleChange = useCallback((_: React.SyntheticEvent, newValue: string | string[] | null) => {
        if (multiple) {
            onChange(newValue as string[]);
        } else {
            // Handle both string and null values
            onChange(newValue as string || '');
        }
    }, [multiple, onChange]);

    const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        const blurInputValue = e.target.value.trim();

        // If input is empty, don't change anything - preserve existing values
        if (blurInputValue === '') {
            return;
        }

        // For freeSolo mode, add the typed value on blur
        if (freeSolo) {
            const capitalizedValue = blurInputValue.charAt(0).toUpperCase() + blurInputValue.slice(1);

            if (multiple) {
                // For multiple values, only add if it's not already in the array
                const currentValues = Array.isArray(value) ? value : [];
                if (!currentValues.includes(capitalizedValue)) {
                    const newValues = [...currentValues, capitalizedValue];
                    onChange(newValues);
                }
            } else {
                onChange(capitalizedValue);
            }
        }
    }, [freeSolo, multiple, value, onChange]);

    // Dynamic styles using sx prop instead of withStyles (which creates new components on each render)
    const autocompleteStyles = useMemo(() => ({
        opacity: disabled ? 0.4 : 1,
        '& .MuiInputLabel-root': {
            color: 'grey',
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: 'grey',
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: 'grey',
            },
            '&:hover fieldset': {
                borderColor: 'grey',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'grey',
            },
        },
        '& .MuiInputBase-input': {
            fontSize: '0.875rem',
            fontFamily: 'primaryFont',
            letterSpacing: 0.3,
            padding: '10px 14px',
        },
        '& .MuiChip-root': {
            backgroundColor: config.primaryColor,
            color: 'white',
            fontSize: '0.75rem',
            fontFamily: 'primaryFont',
            '& .MuiChip-deleteIcon': {
                color: 'white',
                '&:hover': {
                    color: 'rgba(255, 255, 255, 0.7)',
                },
            },
        },
        ...(config.primaryColor !== '#000000' ? {
            '& .MuiInputBase-input:-webkit-autofill': {
                WebkitBoxShadow: `0 0 0 100px ${config.primaryColor}25 inset !important`,
                WebkitTextFillColor: 'inherit',
                caretColor: 'inherit',
            }
        } : {}),
    }), [disabled, config.primaryColor]);

    return (
        <Box sx={{ width: '100%' }}>
            {label && (
                <Typography
                    variant='subtitle2'
                    fontWeight={600}
                    fontFamily='primaryFont'
                    sx={{
                        mb: 0.75,
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        letterSpacing: 0.3,
                    }}
                >
                    {label}
                    {required && <span style={{ color: '#d32f2f', marginLeft: 4 }}>*</span>}
                </Typography>
            )}
            <Autocomplete
                options={groupedOptions.map(x => x.option)}
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                freeSolo={freeSolo}
                fullWidth={fullWidth}
                size={size}
                disabled={disabled}
                multiple={multiple}
                sx={autocompleteStyles}
                groupBy={(option) => option.charAt(0).toUpperCase()}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        onKeyDown={handleKeyDown}
                        placeholder={label}
                    />
                )}
                renderGroup={(params) => (
                    <li key={params.key}>
                        <GroupHeader sx={{ backgroundColor: config.primaryColor, color: 'white' }}>{params.group}</GroupHeader>
                        <GroupItems>{params.children}</GroupItems>
                    </li>
                )}
            />
            {freeSolo && (
                <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{
                        mt: 0.5,
                        display: 'block',
                        fontSize: '0.75rem',
                        fontFamily: 'primaryFont',
                    }}
                >
                    {multiple ? 'Type and press Enter to add items' : 'Type and press Enter to confirm input'}
                </Typography>
            )}
        </Box>
    );
}; 